import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery,
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamVirtualConnectionTablesConnection_nodes,
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery';
import {
  FlowUpstreamVirtualConnectionTablesQuery,
  FlowUpstreamVirtualConnectionTablesQuery_flows_upstreamVirtualConnectionTablesConnection_nodes,
  FlowUpstreamVirtualConnectionTablesQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamVirtualConnectionTablesQuery';
import {
  VirtualConnectionTablesDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionTablesDisplayGridRowDataFragment';
import { VirtualConnectionTableSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryGql';
import { FlowUpstreamVirtualConnectionTablesQueryGql } from '../../queries/FlowUpstreamVirtualConnectionTablesQueryGql';
import { VirtualConnectionTablesDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionTablesDisplayGrid';
import {
  VirtualConnectionTablesValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { getRowDataWithMaxCountsForVirtualConnectionTable } from '../table/functions/VirtualConnectionTableRowDataFunctions';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";

type QueryVariables = FlowUpstreamVirtualConnectionTablesQueryVariables;
type QueryResults = FlowUpstreamVirtualConnectionTablesQuery;
type QueryResultListNode = FlowUpstreamVirtualConnectionTablesQuery_flows_upstreamVirtualConnectionTablesConnection_nodes;
type GridRowData = VirtualConnectionTablesDisplayGridRowData;

type QueryVariablesWithFilter = FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamVirtualConnectionTablesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamVirtualConnectionTablesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamVirtualConnectionTablesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForVirtualConnectionTable(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionTableForFilteredQuery(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowUpstreamVirtualConnectionTablesQueryGridProps =
  GridSortProps<VirtualConnectionTablesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowUpstreamVirtualConnectionTablesQueryGrid(props: FlowUpstreamVirtualConnectionTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamVirtualConnectionTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredVirtualConnectionTablesLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionTablesQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnectionTable}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
